import React from 'react';
import PropTypes from 'prop-types';
import { cn } from 'src/lib/utils';

// "Waiting" cell for the First Contact tab — a big day count, an escalation
// dot trail, and a short status line, matching the reference screenshot.
//
// NOTE: the screenshot implies each candidate has their own deadline (dot
// fill count doesn't move in lockstep with the day count alone), but there
// is no deadline field on our candidate rows to derive that from — so this
// approximates urgency purely from `waiting_days` via fixed bands below.
// Treat the exact thresholds/dot count as a placeholder, not a spec, until
// a real "days since"/"deadline" field is confirmed.
const urgencyFromDays = (days) => {
  if (days == null) {
    return {
      level: 0,
      numberClass: 'text-muted-foreground',
      dotClass: 'bg-muted',
      textClass: 'text-muted-foreground',
    };
  }
  if (days >= 6) {
    return {
      level: 3,
      numberClass: 'text-red-600',
      dotClass: 'bg-red-500',
      textClass: 'text-red-600',
    };
  }
  if (days >= 3) {
    return {
      level: 2,
      numberClass: 'text-amber-600',
      dotClass: 'bg-amber-500',
      textClass: 'text-amber-600',
    };
  }
  return {
    level: 1,
    numberClass: 'text-foreground-strong',
    dotClass: 'bg-muted-foreground/50',
    textClass: 'text-muted-foreground',
  };
};

// Reused by FirstContactTab to color the row's left-edge accent border with
// the same banding, so the table's urgency signal is consistent between
// the cell and the row itself.
export const rowAccentForWaitingDays = (row) => {
  const { level } = urgencyFromDays(row.waiting_days);
  if (level === 3) return 'border-red-400';
  if (level === 2) return 'border-amber-400';
  return null;
};

const WaitingCell = ({ days, statusText }) => {
  const { level, numberClass, dotClass, textClass } = urgencyFromDays(days);

  return (
    <div>
      <div className="flex items-baseline gap-1">
        <span className={cn('text-base font-bold tabular-nums', numberClass)}>
          {days ?? '-'}
        </span>
        <span className="text-xs text-muted-foreground">
          {days === 1 ? 'day' : 'days'}
        </span>
      </div>
      <div className="flex items-center gap-1.5 mt-1">
        <div className="flex items-center gap-0.5">
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              className={cn(
                'w-1.5 h-1.5 rounded-full',
                i < level ? dotClass : 'bg-muted'
              )}
            />
          ))}
        </div>
        {statusText && (
          <span className={cn('text-xs font-medium', textClass)}>
            {statusText}
          </span>
        )}
      </div>
    </div>
  );
};

WaitingCell.propTypes = {
  days: PropTypes.number,
  statusText: PropTypes.string,
};

WaitingCell.defaultProps = {
  days: null,
  statusText: null,
};

export const waitingColumn = {
  key: 'waiting',
  header: 'Waiting',
  className: 'text-sm',
  render: (_v, row) => (
    <WaitingCell days={row.waiting_days} statusText={row.waiting_status_text} />
  ),
};

export default WaitingCell;
